import type { IntradayRow } from '../types/contractHistorical';

/** Coerce chart / API values so Recharts never sees NaN/Infinity. */
export function chartFiniteNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function downsampleUniform<T>(arr: T[], maxPoints: number): T[] {
  if (arr.length <= maxPoints) return arr;
  const step = Math.ceil(arr.length / maxPoints);
  return arr.filter((_, i) => i % step === 0);
}

type IntradayRowLite = { time?: string; datetime?: string };

export function intradayRowTimeMs(row: IntradayRowLite, dayYmd: string): number {
  const dt = row.datetime;
  if (typeof dt === 'string' && dt.length >= 8) {
    const p = Date.parse(dt);
    if (Number.isFinite(p)) return p;
  }
  const day = (dayYmd || '').slice(0, 10);
  const t = (row.time || '12:00:00').trim();
  const hm = t.length >= 8 ? t.slice(0, 8) : '12:00:00';
  const iso = `${day}T${hm}`;
  const u = Date.parse(iso);
  return Number.isFinite(u) ? u : 0;
}

export function intradayAggressorSign(row: {
  execution_side?: string;
  tape_side?: string;
}): -1 | 0 | 1 {
  const ex = String(row.execution_side ?? '').toLowerCase();
  if (ex === 'buy') return 1;
  if (ex === 'sell') return -1;
  if (ex === 'mid') return 0;
  const ts = String(row.tape_side ?? '');
  if (ts === 'at_ask') return 1;
  if (ts === 'at_bid') return -1;
  return 0;
}

type IntradayAggRow = IntradayRowLite & {
  size?: number;
  execution_side?: string;
  tape_side?: string;
};

function intradayFlowQuadrantDelta(right: 'C' | 'P', executionSide: string | undefined): {
  bc: number; sc: number; bp: number; sp: number;
} {
  const ex = String(executionSide ?? '').toLowerCase();
  const z = { bc: 0, sc: 0, bp: 0, sp: 0 };
  if (ex === 'mid' || ex === '') return z;
  if (right === 'C') {
    if (ex === 'buy') z.bc = 1;
    else if (ex === 'sell') z.sc = 1;
  } else {
    if (ex === 'buy') z.bp = 1;
    else if (ex === 'sell') z.sp = 1;
  }
  return z;
}

function mergeIntradayTaggedForTimeSort(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ row: IntradayAggRow; right: 'C' | 'P'; seq: number }> {
  const tagged = [
    ...callRows.map((row, i) => ({ row, right: 'C' as const, seq: i })),
    ...putRows.map((row, i) => ({ row, right: 'P' as const, seq: i })),
  ];
  tagged.sort((a, b) => {
    const da = intradayRowTimeMs(a.row, dayYmd);
    const db = intradayRowTimeMs(b.row, dayYmd);
    if (da !== db) return da - db;
    if (a.right !== b.right) return a.right.localeCompare(b.right);
    return a.seq - b.seq;
  });
  return tagged;
}

export function buildIntradayFlowQuadrantCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; boughtCall: number; soldCall: number; boughtPut: number; soldPut: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let bc = 0; let sc = 0; let bp = 0; let sp = 0;
  const out: Array<{ xKey: string; tickLabel: string; boughtCall: number; soldCall: number; boughtPut: number; soldPut: number }> = [];
  merged.forEach((t, i) => {
    const sz = Math.abs(chartFiniteNumber(t.row.size, 0));
    const d = intradayFlowQuadrantDelta(t.right, t.row.execution_side);
    bc += sz * d.bc; sc += sz * d.sc; bp += sz * d.bp; sp += sz * d.sp;
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    out.push({ xKey: String(i), tickLabel, boughtCall: bc, soldCall: sc, boughtPut: bp, soldPut: sp });
  });
  return out;
}

export function buildIntradayBuySellCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; buyVol: number; sellVol: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let buyVol = 0; let sellVol = 0;
  return merged.map((t, i) => {
    const sz = chartFiniteNumber(t.row.size, 0);
    const ex = String(t.row.execution_side ?? '').toLowerCase();
    if (ex === 'buy') buyVol += Math.abs(sz);
    else if (ex === 'sell') sellVol += Math.abs(sz);
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    return { xKey: String(i), tickLabel, buyVol, sellVol };
  });
}

export function buildIntradayTapeLocationCumulative(
  callRows: IntradayAggRow[],
  putRows: IntradayAggRow[],
  dayYmd: string,
): Array<{ xKey: string; tickLabel: string; atAsk: number; atBid: number; inside: number }> {
  const merged = mergeIntradayTaggedForTimeSort(callRows, putRows, dayYmd);
  let atAsk = 0; let atBid = 0; let inside = 0;
  return merged.map((t, i) => {
    const sz = chartFiniteNumber(t.row.size, 0);
    const ts = String(t.row.tape_side ?? '');
    if (ts === 'at_ask') atAsk += Math.abs(sz);
    else if (ts === 'at_bid') atBid += Math.abs(sz);
    else inside += Math.abs(sz);
    const tickLabel = (t.row.time && String(t.row.time).trim()) || String(i);
    return { xKey: String(i), tickLabel, atAsk, atBid, inside };
  });
}

export function flowTapeAggressionSide(t: { execution_side?: string; side?: string }): 'buy' | 'sell' | 'mid' {
  const s = String(t.execution_side ?? t.side ?? '').toLowerCase();
  if (s === 'buy') return 'buy';
  if (s === 'sell') return 'sell';
  return 'mid';
}

export function formatPremiumMillions(usd: number): string {
  if (!Number.isFinite(usd)) return '—';
  const m = usd / 1e6;
  const abs = Math.abs(m);
  const digits = abs >= 100 ? 1 : abs >= 10 ? 2 : 2;
  return `${m.toFixed(digits)}M`;
}

export function flowTapeRowDate(t: { timestamp?: string; time?: string }): string {
  const ts = t.timestamp;
  if (typeof ts === 'string' && ts.length >= 8) {
    if (ts.length >= 10 && ts[4] === '-' && ts[7] === '-') return ts.slice(0, 10);
    const head = ts.includes('T') ? ts.split('T')[0] : '';
    if (head && /^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
    const digits = ts.replace(/\D/g, '');
    if (digits.length >= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return '—';
}

export type IntradayChartPreparedRow = IntradayRow & {
  chartIndex: number;
  xKey: string;
  tickLabel: string;
  absPremiumUsd: number;
  signedPremiumUsd: number;
  sizeForBar: number;
  directionBarColor: string;
};

export function prepareIntradayChartRows(
  intradayData: IntradayRow[],
  dayYmd: string,
  pointLimit: number,
  colors: { buy: string; sell: string; neutral: string },
): IntradayChartPreparedRow[] {
  let indexed = intradayData.map((row, i) => ({ row, i }));
  indexed.sort((a, b) => {
    const da = intradayRowTimeMs(a.row, dayYmd);
    const db = intradayRowTimeMs(b.row, dayYmd);
    if (da !== db) return da - db;
    return a.i - b.i;
  });
  if (indexed.length > pointLimit) {
    const step = Math.ceil(indexed.length / pointLimit);
    indexed = indexed.filter((_, i) => i % step === 0);
  }
  return indexed.map(({ row }, chartIndex) => {
    const priceN = Number(row.price);
    const sizeN = Number(row.size);
    const price = Number.isFinite(priceN) ? priceN : undefined;
    const size = Number.isFinite(sizeN) ? sizeN : undefined;
    const absPrem = chartFiniteNumber(row.premium ?? (Number(price) || 0) * (Number(size) || 0) * 100, 0);
    const sign = intradayAggressorSign(row);
    const signedPremiumUsd = sign === 0 ? 0 : sign * absPrem;
    const dirColor = sign === 1 ? colors.buy : sign === -1 ? colors.sell : colors.neutral;
    const tickLabel = (row.time && String(row.time).trim()) || String(chartIndex);
    return {
      ...row,
      price,
      size,
      chartIndex,
      xKey: String(chartIndex),
      tickLabel,
      absPremiumUsd: absPrem,
      signedPremiumUsd,
      sizeForBar: Math.abs(Number(size) || 0),
      directionBarColor: dirColor,
    };
  });
}

export function buildIntradayTapeBarRow(summary: { bid_side_contracts?: number; between_contracts?: number; ask_side_contracts?: number } | null) {
  if (!summary) return [];
  return [{
    label: 'Session',
    atBid: Number(summary.bid_side_contracts) || 0,
    between: Number(summary.between_contracts) || 0,
    atAsk: Number(summary.ask_side_contracts) || 0,
  }];
}
